import React, { useState } from "react";
import { Button } from "../../ui/button";
import { useEditorContext } from "../../../contexts/editor-context";
import { CaptionOverlay, Overlay, OverlayType } from "../../../types";
import { CaptionSettings } from "./caption-settings";
import { CaptionsErrorDisplay } from "./captions-error-display";
import { useCaptions } from "../../../hooks/use-captions";
import { useTimelineCaptions } from "../../../hooks/use-timeline-captions";
import { useUploadStatus } from "../../../hooks/use-upload-status";
import { LanguageSelector } from "./language-selector";
import { Loader2, AlertTriangle, Sparkles, Upload } from "lucide-react";
import { Alert, AlertDescription } from "../../ui/alert";
import { getFontForLanguage } from "../../../utils/captions/language-font-map";
import { DEFAULT_CAPTION_STYLE } from "../../../templates/caption-templates";

/**
 * CaptionsOverlayPanel Component
 *
 * @component
 * @description
 * Clean interface for managing captions in the video editor.
 * Provides functionality for:
 * - Uploading SRT caption files with validation
 * - Manual script entry with automatic timing
 * - Caption editing and styling
 * - Error handling and user guidance
 *
 * Features:
 * - SRT file parsing with comprehensive validation
 * - Real-time error feedback
 * - Text-to-caption conversion
 * - Automatic timeline positioning
 * - Clean, intuitive UI
 *
 * @example
 * ```tsx
 * <CaptionsOverlayPanel />
 * ```
 */
export const CaptionsOverlayPanel: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("auto");
  
  const {
    overlays,
    selectedOverlayId,
    changeOverlay,
    currentFrame,
  } = useEditorContext();

  const {
    isProcessing,
    isError,
    error,
    lastParseResult,
    handleFileUpload,
    createCaptionOverlay,
    replaceCaptions,
    reset,
  } = useCaptions();

  const {
    isGenerating,
    generationError,
    lastGenerationResult,
    generateCaptions,
    reset: resetTimelineCaptions,
  } = useTimelineCaptions();

  // Track upload status for video overlays
  const { hasPendingUploads, pendingCount } = useUploadStatus(overlays);

  const [localOverlay, setLocalOverlay] = useState<CaptionOverlay | null>(null);

  // Find existing caption overlay
  const existingCaptionOverlay = overlays.find(
    (overlay) => overlay.type === OverlayType.CAPTION
  ) as CaptionOverlay | undefined;

  React.useEffect(() => {
    if (selectedOverlayId === null) {
      setLocalOverlay(null);
      return;
    }

    const selectedOverlay = overlays.find(
      (overlay) => overlay.id === selectedOverlayId
    );

    if (selectedOverlay?.type === OverlayType.CAPTION) {
      setLocalOverlay(selectedOverlay as CaptionOverlay);
    }
  }, [selectedOverlayId, overlays]);

  const handleUpdateOverlay = (updatedOverlay: CaptionOverlay) => {
    setLocalOverlay(updatedOverlay);
    changeOverlay(updatedOverlay.id, () => updatedOverlay);
  };

  const handleGenerateOrUpdateCaptions = async () => {
    reset();
    resetTimelineCaptions();

    try {
      const language = selectedLanguage === "auto" ? undefined : selectedLanguage;
      const result = await generateCaptions(language);

      if (result.success && result.captions) {
        // Determine effective language (explicit selection or auto-detected)
        const effectiveLanguage = selectedLanguage !== "auto"
          ? selectedLanguage
          : result.language;

        // Look up the appropriate font for this language
        const font = effectiveLanguage ? getFontForLanguage(effectiveLanguage) : undefined;
        const fontFamily = font || DEFAULT_CAPTION_STYLE.fontFamily;

        if (existingCaptionOverlay) {
          // Update existing captions and font
          replaceCaptions(existingCaptionOverlay.id, result.captions);
          const currentStyles = existingCaptionOverlay.styles || DEFAULT_CAPTION_STYLE;
          changeOverlay(existingCaptionOverlay.id, (overlay) => ({
            ...overlay,
            styles: { ...currentStyles, fontFamily },
          }) as Overlay);
        } else {
          // Create new caption overlay with font override
          createCaptionOverlay(result.captions, { fontFamily });
        }
      }
    } catch (error) {
      console.error('Caption generation error:', error);
    }
  };

  const handleRetry = () => {
    reset();
    resetTimelineCaptions();
  };

  const isProcessingAny = isProcessing || isGenerating;
  const displayError = generationError || error;

  return (
    <div className="flex flex-col gap-4 p-2 h-full w-full max-w-full overflow-hidden [&_[data-radix-scroll-area-viewport]]:!scrollbar-none">
      {!localOverlay ? (
        <>
          {/* Show errors if any */}
          {lastParseResult && !lastParseResult.success && lastParseResult.errors && (
            <CaptionsErrorDisplay
              errors={lastParseResult.errors}
              onRetry={handleRetry}
            />
          )}

          <div className="shrink-0 space-y-4">
            {/* Language Selector */}
            <LanguageSelector
              value={selectedLanguage}
              onValueChange={setSelectedLanguage}
              disabled={isProcessingAny}
            />

            {/* Upload Status Warning */}
            {hasPendingUploads && !isProcessingAny && (
              <Alert className="border-blue-500/50 bg-blue-500/5">
                <Upload className="w-4 h-4 text-blue-500" />
                <AlertDescription className="text-blue-700 dark:text-blue-400">
                  {pendingCount} video{pendingCount > 1 ? 's' : ''} uploading to cloud storage...
                  Caption generation will wait for uploads to complete.
                </AlertDescription>
              </Alert>
            )}

            {/* Generate/Update Captions Button */}
            <Button
              onClick={handleGenerateOrUpdateCaptions}
              className="w-full"
              size="lg"
              disabled={isProcessingAny}
            >
              {isProcessingAny ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isGenerating ? 'Generating Captions...' : 'Processing...'}
                </>
              ) : hasPendingUploads ? (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Generate Captions (Will Wait for Uploads)
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {existingCaptionOverlay ? 'Update Captions' : 'Generate Captions'}
                </>
              )}
            </Button>

            {/* Status Messages */}
            {isGenerating && (
              <div className="text-xs text-muted-foreground text-center space-y-1">
                <p>Preparing media files for transcription...</p>
                <p>Rendering timeline composition...</p>
                <p>Transcribing audio with ElevenLabs...</p>
                <p className="text-[10px]">This may take a few moments</p>
              </div>
            )}

            {/* Success Message */}
            {lastGenerationResult?.success && (
              <Alert className="border-green-500/50 bg-green-500/5">
                <AlertDescription className="text-green-700 dark:text-green-400">
                  Captions generated successfully! {lastGenerationResult.captions?.length} captions created.
                  {lastGenerationResult.language && ` Detected language: ${lastGenerationResult.language}`}
                </AlertDescription>
              </Alert>
            )}

            {/* Error Display */}
            {displayError && (
              <Alert className="border-destructive/50 bg-destructive/5">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <AlertDescription className="text-destructive">
                  {displayError}
                </AlertDescription>
              </Alert>
            )}

            {/* General Error Display (from SRT parsing) */}
            {isError && error && !lastParseResult?.errors && !generationError && (
              <Alert className="border-destructive/50 bg-destructive/5">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <AlertDescription className="text-destructive">
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-hidden w-full max-w-full">
          <CaptionSettings
            currentFrame={currentFrame}
            localOverlay={localOverlay}
            setLocalOverlay={handleUpdateOverlay}
            startFrame={localOverlay.from}
            captions={localOverlay.captions}
          />
        </div>
      )}
    </div>
  );
};
